import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { StyledAnchor } from "shared/components/common/base-link/styles";
import { fontMerriweather, fontRoboto, setFullViewportWidthStyles, theme } from "shared/lib/styles";

import {
  AdSlotContainer,
  Label as AdSlotLabel,
  Container as AdSlotOuterContainer,
} from "scmp-app/components/advertisement/ad-slots/ad-slot/styles";
import {
  AndSeparator,
  AuthorLocation,
  AuthorNamesContainer,
  CommaSeparator,
} from "scmp-app/components/article/article-author-block/author-names/styles";
import { ArticleGoogleNewsButton } from "scmp-app/components/article/article-google-news-button";
import { ArticleHeaderAdSlot } from "scmp-app/components/article/article-header-ad-slot";
import { pageStyle } from "scmp-app/components/article/article-render/article-content/standard-styles";
import {
  SectionLink,
  SectionTagsSpacer,
  VideoLink,
} from "scmp-app/components/article/article-sections/styles";
import { StyledTopicLink } from "scmp-app/components/article/article-topic/styles";
import {
  StyledEntityLink as AuthorNameEntityLink,
  StyledAuthorName,
} from "scmp-app/components/author/author-name/styles";
import { StyledParagraph } from "scmp-app/components/schema-render/common/paragraph/styles";
import { SCMPYoutubeVideo } from "scmp-app/components/scmp-youtube-video";

import { UpNextWidget } from "./up-next-widget";

export const StyledArticleGoogleNewsButton = styled(ArticleGoogleNewsButton)``;

export const StyledUpNextWidget = styled(UpNextWidget)``;

export const StyledSCMPYoutubeVideo = styled(SCMPYoutubeVideo)`
  --portrait-inline-size: 100%;

  inline-size: 100%;
  block-size: 100%;

  border-radius: 12px;

  overflow: hidden;
`;

type PlayerContainerProps = {
  $isFirstArticle: boolean;
  $isPortrait: boolean;
};

export const PlayerContainer = styled.div<PlayerContainerProps>`
  grid-area: player;

  box-sizing: content-box;

  ${props =>
    props.$isPortrait
      ? css`
          inline-size: 100%;
          max-inline-size: 440px;
          aspect-ratio: 9 / 16;

          margin-block-start: 20px;
          margin-inline: auto;

          ${theme.breakpoints.up("tablet")} {
            inline-size: 320px;
            margin-block-start: 32px;
          }

          ${theme.breakpoints.up("desktop")} {
            inline-size: 400px;
          }
        `
      : css`
          aspect-ratio: 16 / 9;

          inline-size: 100%;

          ${theme.breakpoints.only("mobile")} {
            ${StyledSCMPYoutubeVideo} {
              border-radius: 0;
            }

            ${setFullViewportWidthStyles()}
          }

          ${theme.breakpoints.up("tablet")} {
            margin-block-start: 40px;
          }
        `}

  ${props =>
    !props.$isPortrait &&
    !props.$isFirstArticle &&
    css`
      margin-block-start: 32px;
    `}
`;

export const DesktopHeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: desktop-header-ad;

  display: none;

  background-color: #f8f8f8;

  ${theme.breakpoints.up("desktop")} {
    display: block;

    margin-inline: calc(50% - 50vw);

    background-color: #f8f8f8;
  }

  ${AdSlotContainer} {
    display: flex;
    align-items: center;
  }
`;

export const FooterAd = styled.div`
  grid-area: footer-ad;

  ${setFullViewportWidthStyles()}

  margin-block: 28px 12px;
  padding-block-end: 12px;
  border-block: 1px solid #dfdfdf;

  background-color: #f8f8f8;

  ${AdSlotLabel} {
    color: #717171;
    font-size: 10px;
    text-transform: uppercase;
  }

  ${theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const TopicAndSection = styled.div`
  grid-area: topic-and-section;

  display: flex;
  flex-flow: row wrap;
  gap: 16px;
  align-items: flex-start;

  margin-block-start: 20px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }

  ${SectionTagsSpacer} {
    margin-inline: 4px;
  }

  ${SectionLink}, ${VideoLink} {
    font-size: 12px;
    line-height: normal;

    ${theme.breakpoints.up("tablet")} {
      font-size: 14px;
    }

    span {
      color: #000000;
      font-weight: 400;
      font-size: inherit;
      font-family: ${fontRoboto};
      font-style: italic;
      line-height: inherit;
    }

    span:hover {
      color: #2c4692;
    }
  }

  ${StyledTopicLink} {
    :hover {
      color: #2c4692;
    }
  }
`;

export const Headline = styled.div`
  grid-area: headline;

  margin-block-start: 12px;

  color: #000000;
  font-weight: 600;
  font-size: 22px;
  font-family: ${fontMerriweather};
  font-style: normal;
  line-height: 30.8px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;

    font-size: 28px;
    line-height: 39.2px;
  }

  ${theme.breakpoints.up("desktop")} {
    font-size: 36px;
    line-height: 50.4px;
  }
`;

export const SubHeadline = styled.div`
  grid-area: sub-headline;

  margin-block-start: 12px;

  color: #333333;
  font-weight: 400;
  font-size: 16px;
  font-family: ${fontMerriweather};
  font-style: normal;
  line-height: 24px;
  font-variant-numeric: lining-nums proportional-nums;

  :empty {
    display: none;
  }

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;

    font-size: 20px;
    line-height: 30px;
  }
`;

export const ActionWidgets = styled.div`
  grid-area: action-widgets;

  display: flex;
  flex-flow: row wrap;
  gap: 16px;
  align-items: center;

  margin-block-start: 12px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
  }
`;

export const Author = styled.div`
  grid-area: author;

  border-block-start: 1px solid #000000;

  margin-block-start: 20px;
  padding-block-start: 12px;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    margin-block-start: 32px;
    padding-block-start: 16px;
  }

  ${AuthorNamesContainer} {
    line-height: normal;

    ${StyledAuthorName}, ${AuthorNameEntityLink}, ${AuthorLocation}, ${CommaSeparator}, ${AndSeparator} {
      color: #000000;
      font-size: 14px;
      font-family: ${fontRoboto};
      font-style: normal;
      line-height: normal;
      font-variant-numeric: lining-nums proportional-nums;
    }

    ${AuthorLocation} {
      color: #000000;
      font-weight: 400;
    }

    ${CommaSeparator}, ${AndSeparator} {
      font-weight: 700;
    }

    ${StyledAuthorName}, ${AuthorNameEntityLink} {
      text-decoration-line: underline;

      font-weight: 700;
    }
  }
`;

export const PublishDateTime = styled.div`
  grid-area: publish-datetime;

  margin-block-start: 8px;

  line-height: normal;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    margin-inline: auto;
  }
`;

export const Content = styled.div`
  grid-area: content;

  ${props => pageStyle(props)}

  inline-size: 100%;
  margin-block-start: 16px;
  margin-inline: auto;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-block-start: 32px;
  }

  ${StyledParagraph} {
    font-size: 16px;
    line-height: 160%;

    ${theme.breakpoints.up("desktop")} {
      font-size: 18px;
    }
  }

  ${StyledAnchor} {
    color: #2c4692;
    font-weight: 400;
    font-size: 16px;
    font-family: ${fontMerriweather};
    line-height: 28.8px;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }

    ${theme.breakpoints.up("desktop")} {
      font-size: 18px;
      line-height: 32.4px;
    }
  }
`;

export const AuthorDetail = styled.div`
  grid-area: author-detail;

  margin-block-start: 12px;
  padding-block-start: 16px;

  border-block-start: 1px #000000 solid;
`;

export const RelatedTopic = styled.div`
  grid-area: related-topic;

  margin-block-start: 18px;
  padding-block-start: 12px;

  border-block-start: 1px #dfdfdf solid;

  &:empty {
    display: none;
  }
`;

export const RightSectionContainer = styled.div`
  grid-area: right;

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-block-start: 32px;

    ${AdSlotOuterContainer} {
      justify-content: flex-start;

      block-size: auto;
    }
  }
`;

export const UpNextWidgetContainer = styled.div`
  grid-area: up-next-widget;

  margin-block-start: 16px;
  padding-block: 12px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
    padding-block-start: 0;
  }
`;

type ContainerProps = {
  $isPortrait: boolean;
};

export const Container = styled.div<ContainerProps>`
  display: grid;
  grid:
    "player" min-content
    "topic-and-section" min-content
    "headline" min-content
    "sub-headline" min-content
    "action-widgets" min-content
    "author" min-content
    "publish-datetime" min-content
    "content" min-content
    "footer-ad" min-content
    "author-detail" min-content
    "related-topic" min-content
    "up-next-widget" min-content
    / 100%;

  inline-size: 100%;
  margin-inline: auto;
  padding-block-start: 40px;

  ${props =>
    props.$isPortrait &&
    css`
      grid:
        "topic-and-section" min-content
        "headline" min-content
        "sub-headline" min-content
        "action-widgets" min-content
        "author" min-content
        "publish-datetime" min-content
        "footer-ad" min-content
        "player" min-content
        "content" min-content
        "author-detail" min-content
        "related-topic" min-content
        "up-next-widget" min-content
        "bottom-carousel" min-content
        / 100%;

      ${TopicAndSection} {
        ${theme.breakpoints.up("desktop")} {
          margin-block-start: 40px;
        }
      }

      ${RightSectionContainer} {
        ${theme.breakpoints.up("desktop")} {
          margin-block-start: 40px;
        }
      }
    `}

  ${theme.breakpoints.only("mobile")} {
    --mobile-widget-gap: 16px;
  }

  ${theme.breakpoints.up("tablet")} {
    grid:
      "player player player" min-content
      ". topic-and-section ." min-content
      ". headline ." min-content
      ". sub-headline ." min-content
      ". action-widgets ." min-content
      ". author ." min-content
      ". publish-datetime ." min-content
      ". footer-ad ." min-content
      ". content ." min-content
      ". author-detail ." min-content
      ". related-topic ." min-content
      "up-next-widget up-next-widget up-next-widget" min-content
      / minmax(0, 1fr) 600px minmax(0, 1fr);

    ${props =>
      props.$isPortrait &&
      css`
        grid:
          ". topic-and-section ." min-content
          ". headline ." min-content
          ". sub-headline ." min-content
          ". action-widgets ." min-content
          ". author ." min-content
          ". publish-datetime ." min-content
          ". footer-ad ." min-content
          ". player ." min-content
          ". content ." min-content
          ". author-detail ." min-content
          ". related-topic ." min-content
          "up-next-widget up-next-widget up-next-widget" min-content
          / minmax(0, 1fr) 600px minmax(0, 1fr);
      `}
  }

  ${theme.breakpoints.up("desktop")} {
    grid:
      "desktop-header-ad desktop-header-ad desktop-header-ad desktop-header-ad" min-content
      "player player player player" min-content
      ". topic-and-section . right" min-content
      ". headline . right" min-content
      ". sub-headline . right" min-content
      ". action-widgets . right" min-content
      ". author . right" min-content
      ". publish-datetime . right" min-content
      ". content . right" min-content
      ". author-detail . right" min-content
      ". related-topic . right" min-content
      "up-next-widget up-next-widget up-next-widget up-next-widget" min-content
      / minmax(0, 1fr) 600px minmax(0, 1fr) 300px;

    ${props =>
      props.$isPortrait &&
      css`
        grid:
          "desktop-header-ad desktop-header-ad desktop-header-ad desktop-header-ad" min-content
          ". topic-and-section . right" min-content
          ". headline . right" min-content
          ". sub-headline . right" min-content
          ". action-widgets . right" min-content
          ". author . right" min-content
          ". publish-datetime . right" min-content
          ". player . right" min-content
          ". content . right" min-content
          ". author-detail . right" min-content
          ". related-topic . right" min-content
          "up-next-widget up-next-widget up-next-widget up-next-widget" min-content
          / minmax(0, 1fr) 600px minmax(0, 1fr) 300px;
      `}

    max-inline-size: 1288px;
  }
`;
